import axios, { AxiosRequestConfig } from 'axios';

// ─── Types ────────────────────────────────────────────────────────────

export type BulkJobStatus = 'generating' | 'review' | 'applying' | 'done' | 'partial' | 'cancelled';
/** `processing`/`applying` : item réservé par un `advance` en cours (cf. api/src/types/BulkJob.ts) */
export type BulkItemStatus =
    | 'pending' | 'processing' | 'generated' | 'accepted' | 'applying' | 'rejected' | 'applied' | 'error';

export interface BulkJob {
    _id: string;
    /** `document` (C19) : un lot de préparation de documents pour l'assistant */
    targetType: 'note' | 'entree' | 'perimetre' | 'thesaurus' | 'document';
    summary: string;
    status: BulkJobStatus;
    progress: { generated: number; applied: number; errors: number };
    resolvedCount: number;
    operation: { kind: string } & Record<string, unknown>;
    /** Phrase humaine de fin de lot (plafond du jour, coupe-circuit) — lot d'indexation (C19) */
    message?: string;
}

/** Décision d'acceptation partielle portée par un item `tag-entrees` (cf. design § 3.2) */
export interface TagEntreesDecision {
    /** Entrées entièrement refusées pour cette note */
    excludedEntreeIds: string[];
    /** Ordinaux `i` de passages refusés individuellement */
    excludedSpans: number[];
}

/** Un passage détecté, tel qu'exposé par `after.passages` d'un item `tag-entrees` (design § 3.1) */
export interface TagPassage {
    i: number;
    entreeId: string;
    cle: string;
    bucket: string;
    confidence: number;
    excerpt: string;
}

export interface BulkJobItem {
    _id: string;
    targetId: string;
    targetLabel: string;
    status: BulkItemStatus;
    // Les deux côtés sont optionnels À LA LECTURE : les lots créés avant le
    // passage de `BulkJobItem` en `minimize: false` ont perdu en base leur côté
    // vide (`after: {}` d'une suppression). Le rendu doit y survivre.
    preview?: { before?: Record<string, unknown>; after?: Record<string, unknown> };
    error?: string;
    decision?: TagEntreesDecision;
}

/** Nom du champ de préversion portant les passages (`after.passages`), filtré des listes génériques */
export const TAG_PASSAGES_FIELD = 'passages';

/** `after.passages` de l'item si c'est un tableau, sinon `null` (items non `tag-entrees` ou anciens) */
export function passagesOf(item: BulkJobItem): TagPassage[] | null {
    const passages = item.preview?.after?.[TAG_PASSAGES_FIELD];
    return Array.isArray(passages) ? (passages as TagPassage[]) : null;
}

// ─── Config ───────────────────────────────────────────────────────────

const createConfig = (accessToken: string): AxiosRequestConfig => ({
    headers: {
        'Authorization': `Bearer ${accessToken}`,
        'AccessToken': accessToken,
        'Content-Type': 'application/json'
    }
});

// ─── Bulk Jobs API ────────────────────────────────────────────────────

export const bulkJobsAPI = {
    /**
     * Crée un lot d'opérations en masse (canal UI).
     */
    create: async (
        params: { targetType: string; selection: unknown; operation: unknown; summary?: string },
        accessToken: string
    ): Promise<BulkJob> => {
        try {
            const response = await axios.post('/api/bulk-jobs', params, createConfig(accessToken));
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Erreur de création du lot');
        }
    },

    /**
     * Récupère un lot et ses items (paginés).
     */
    get: async (
        jobId: string,
        accessToken: string,
        itemsOffset = 0,
        itemsLimit = 50
    ): Promise<{ job: BulkJob; items: BulkJobItem[]; itemsTotal: number }> => {
        try {
            const response = await axios.get(
                `/api/bulk-jobs/${jobId}?itemsOffset=${itemsOffset}&itemsLimit=${itemsLimit}`,
                createConfig(accessToken)
            );
            return response.data.data;
        } catch (error: any) {
            if (error.response?.status === 404) {
                throw new Error('Lot introuvable');
            }
            throw new Error(error.response?.data?.message || 'Erreur de chargement du lot');
        }
    },

    /**
     * Avance le traitement d'un lot (génération/application).
     */
    advance: async (jobId: string, accessToken: string): Promise<{ job: BulkJob; processed: number }> => {
        try {
            const response = await axios.post(`/api/bulk-jobs/${jobId}/advance`, {}, createConfig(accessToken));
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Erreur d\'avancement du lot');
        }
    },

    /**
     * Met à jour le statut d'items (accepted/rejected).
     */
    updateItems: async (
        jobId: string,
        params: { itemIds?: string[]; all?: boolean; status: 'accepted' | 'rejected'; decision?: TagEntreesDecision },
        accessToken: string
    ): Promise<{ modified: number }> => {
        try {
            const response = await axios.patch(`/api/bulk-jobs/${jobId}/items`, params, createConfig(accessToken));
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Erreur de mise à jour des items');
        }
    },

    /**
     * Démarre l'application d'un lot en revue.
     */
    apply: async (jobId: string, accessToken: string): Promise<{ job: BulkJob }> => {
        try {
            const response = await axios.post(`/api/bulk-jobs/${jobId}/apply`, {}, createConfig(accessToken));
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Erreur d\'application du lot');
        }
    },

    /**
     * Annule un lot encore modifiable.
     */
    cancel: async (jobId: string, accessToken: string): Promise<{ job: BulkJob }> => {
        try {
            const response = await axios.post(`/api/bulk-jobs/${jobId}/cancel`, {}, createConfig(accessToken));
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Erreur d\'annulation du lot');
        }
    },
};
